// absolute imports
import { useNavigate } from '@tanstack/react-router';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo } from 'react';

// relative imports
import { modalStateVar } from '../../data/apollo/cache/reactiveVars';
import updateNestedVar from '../../data/apollo/cache/utilities/updateNestedVar';
import { dispatchToDataLayer } from '../shared/google-tag-manager/GTMContainer';
import { stringToBitwiseXORHash } from '../shared/utilities/stringUtilities';

export const ModalActionsContext = createContext();
export const useModalActions = () => useContext(ModalActionsContext);

export const ModalActionsProvider = ({ children }) => {
  const navigate = useNavigate();

  const hideModal = useCallback(
    ({ modalId, shouldPushHistory = false, onHide } = {}) => {
      navigate({
        replace: !shouldPushHistory,
        search: (prev) => {
          const newSearch = { ...prev };

          if (prev?.msids?.length === 1) {
            delete newSearch.msids;
          } else if (!prev.msids) {
            newSearch.msids = undefined;
          } else if (modalId) {
            newSearch.msids = prev.msids.filter((msid) => msid !== modalId);
          } else {
            newSearch.msids = prev.msids.slice(0, -1);
          }

          return newSearch;
        },
      }).then(() => {
        if (onHide) {
          onHide();
        }
      });
    },
    [navigate],
  );

  const showModal = useCallback(
    (
      modalId,
      props,
      // shouldUpdateHistory = true,
    ) => {
      const modalIdHash = String(stringToBitwiseXORHash(modalId));

      updateNestedVar(modalStateVar, modalIdHash, {
        id: modalId,
        props,
      });

      // dispatch modal opening event on modal open
      if (!window.Cypress) {
        const dataLayerArgs = {
          dataLayer: {
            event: 'ModalOpen',
            modalName: modalId,
          },
        };

        dispatchToDataLayer(dataLayerArgs);
      }

      navigate({
        search: (prev) => ({
          ...prev,
          msids: prev.msids ? [...prev.msids, modalIdHash] : [modalIdHash],
        }),
      });
    },
    [navigate],
  );

  const modalActionsValue = useMemo(
    () => ({
      hideModal,
      showModal,
    }),
    [hideModal, showModal],
  );

  return (
    <ModalActionsContext.Provider value={modalActionsValue}>
      {children}
    </ModalActionsContext.Provider>
  );
};

export const ModalActionContextConsumer = ModalActionsContext.Consumer;

ModalActionsProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.shape()]).isRequired,
};
